import { useEffect, useState, type ReactNode } from 'react';
import { Animated, Platform, View, type StyleProp, type ViewStyle } from 'react-native';
import { useThemeColors } from '../hooks/useThemeColors';

export type SkeletonVariant = 'text' | 'line' | 'block' | 'circle' | 'card';

export interface SkeletonProps {
  /** Width in px or percentage string. Defaults to `'100%'`. */
  width?: number | `${number}%`;
  /** Height in px. Defaults vary by variant — text=14, line=1, block=80, circle uses width. */
  height?: number;
  /** Corner radius. Defaults vary by variant. */
  borderRadius?: number;
  /** Composition shape. `card` renders a header line + body lines + image block. */
  variant?: SkeletonVariant;
  /** Extra style overrides (margin, alignSelf, etc.). */
  style?: StyleProp<ViewStyle>;
  /** Disable the pulse animation. Useful in tests / snapshots. */
  animate?: boolean;
  /**
   * Optional accessibility label. When set, the skeleton announces itself
   * to screen readers as a `progressbar` with this label. Leave undefined
   * (the default) when the skeleton is one of many inside a labelled
   * loading region — wrap them in `Skeleton.Group` instead so VoiceOver /
   * TalkBack doesn't repeat "Loading" once per block.
   */
  accessibilityLabel?: string;
}

const VARIANT_DEFAULTS: Record<SkeletonVariant, { height: number; borderRadius: number }> = {
  text: { height: 14, borderRadius: 6 },
  line: { height: 1, borderRadius: 0 },
  block: { height: 80, borderRadius: 10 },
  circle: { height: 0, borderRadius: 9999 }, // width = height enforced in render
  card: { height: 120, borderRadius: 12 }, // approximate; composite below
};

interface SkeletonBlockProps {
  width: number | `${number}%`;
  height: number;
  borderRadius: number;
  animate: boolean;
  style?: StyleProp<ViewStyle>;
  background: string;
}

function SkeletonBlock({ width, height, borderRadius, animate, style, background }: SkeletonBlockProps) {
  const [opacity] = useState(() => new Animated.Value(0.6));

  useEffect(() => {
    if (!animate) return undefined;
    const useNative = Platform.OS !== 'web';
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, { toValue: 1, duration: 700, useNativeDriver: useNative }),
        Animated.timing(opacity, { toValue: 0.5, duration: 700, useNativeDriver: useNative }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [animate, opacity]);

  return (
    <Animated.View
      style={[
        {
          width,
          height,
          borderRadius,
          backgroundColor: background,
          opacity: animate ? opacity : 0.6,
        },
        style,
      ]}
    />
  );
}

/**
 * Pulsing placeholder block used while data is loading.
 *
 * Variants per ADR-0074:
 * - `text` (default): inline text-height block, 14×100%, radius 6.
 * - `line`: 1px-tall full-width divider placeholder.
 * - `block`: 80px-tall solid block — for image / card preview slots.
 * - `circle`: equal-side avatar placeholder; pass `width` for size.
 * - `card`: composite — image block + two text lines.
 *
 * Wrap groups of skeletons in `Skeleton.Group` so the announcement
 * happens once, not per block.
 */
function SkeletonImpl({
  width = '100%',
  height,
  borderRadius,
  variant = 'text',
  style,
  animate = true,
  accessibilityLabel,
}: SkeletonProps) {
  const theme = useThemeColors();
  const background = theme.dividerSubtle;

  // Only expose role + label when the caller asks — composite skeletons
  // carry the label on Skeleton.Group instead.
  const a11y = accessibilityLabel
    ? { accessibilityRole: 'progressbar' as const, accessibilityLabel }
    : null;

  if (variant === 'card') {
    return (
      <View {...a11y} style={style}>
        <SkeletonBlock
          width={width}
          height={height ?? VARIANT_DEFAULTS.card.height}
          borderRadius={borderRadius ?? VARIANT_DEFAULTS.card.borderRadius}
          animate={animate}
          background={background}
        />
        <View style={{ height: 8 }} />
        <SkeletonBlock
          width="60%"
          height={VARIANT_DEFAULTS.text.height}
          borderRadius={VARIANT_DEFAULTS.text.borderRadius}
          animate={animate}
          background={background}
        />
        <View style={{ height: 4 }} />
        <SkeletonBlock
          width="40%"
          height={VARIANT_DEFAULTS.text.height}
          borderRadius={VARIANT_DEFAULTS.text.borderRadius}
          animate={animate}
          background={background}
        />
      </View>
    );
  }

  const defaults = VARIANT_DEFAULTS[variant];
  const resolvedHeight = (variant === 'circle' && typeof width === 'number')
    ? width
    : (height ?? defaults.height);
  const resolvedBorderRadius = borderRadius ?? defaults.borderRadius;

  // When the caller supplied an accessibilityLabel, wrap in a View so
  // the a11y attributes actually attach — the inner SkeletonBlock is
  // an Animated.View that can't carry props without going through the
  // wrapper. Without the wrapper, the label was silently dropped for
  // every variant other than `card`.
  if (a11y) {
    return (
      <View {...a11y} style={style}>
        <SkeletonBlock
          width={width}
          height={resolvedHeight}
          borderRadius={resolvedBorderRadius}
          animate={animate}
          background={background}
        />
      </View>
    );
  }

  return (
    <SkeletonBlock
      width={width}
      height={resolvedHeight}
      borderRadius={resolvedBorderRadius}
      animate={animate}
      background={background}
      style={style}
    />
  );
}

export interface SkeletonGroupProps {
  /**
   * One label for the whole loading region — screen readers announce
   * this once, not per child block (per `Skeleton.tsx:20-22` convention).
   */
  accessibilityLabel: string;
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
}

/**
 * Wraps a group of skeleton blocks under a single announced loading
 * region. Use this whenever a screen shows more than one Skeleton — a
 * trip card list, a place-detail page, a settings shell — so VoiceOver
 * / TalkBack doesn't say "Loading" once per block.
 */
function SkeletonGroup({ accessibilityLabel, children, style }: SkeletonGroupProps) {
  return (
    <View
      accessibilityRole="progressbar"
      accessibilityLabel={accessibilityLabel}
      style={style}
    >
      {children}
    </View>
  );
}

// Named export with Group attached as a static property — mirrors the
// `Card.Header` / `Card.Body` composition pattern used elsewhere in
// libs/ui.
type SkeletonComponent = typeof SkeletonImpl & { Group: typeof SkeletonGroup };
export const Skeleton = SkeletonImpl as SkeletonComponent;
Skeleton.Group = SkeletonGroup;
